import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  ShoppingCart as ShoppingCartIcon,
  AutoFixHigh as AutoFixHighIcon,
  Videocam as VideocamIcon,
  PhotoCamera as PhotoCameraIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import RichTextEditor from './RichTextEditor';
import { marketingService, Product } from '../services/marketingService';
import LeadSelectionDialog from './LeadSelectionDialog';
import { Lead } from '../redux/slices/leadSlice';
import { useDispatch } from 'react-redux';
import { updateLead } from '../redux/slices/leadSlice';
import { useCreateNotification } from '../hooks/useCreateNotification';
import { NOTIFICATION_TYPES } from '../utils/notificationUtils';

interface MediaFile {
  id: string;
  file: File;
  type: 'image' | 'video';
  preview: string;
}

const CampaignSection: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    type: 'email' as 'email' | 'whatsapp',
    subject: '',
    content: '',
    recipients: [] as string[],
    scheduledDates: [] as string[],
    sendNow: false,
  });
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [leadSelectionDialogOpen, setLeadSelectionDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [createdCampaign, setCreatedCampaign] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const createNotification = useCreateNotification();

  const handleMediaUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles = Array.from(files).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      type: file.type.startsWith('image/') ? 'image' as const : 'video' as const,
      preview: URL.createObjectURL(file),
    }));

    setMediaFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemoveMedia = (id: string) => {
    setMediaFiles(prev => prev.filter(file => file.id !== id));
  };

  const handleAddSchedule = (date: Date | null) => {
    if (date) {
      setNewCampaign(prev => ({
        ...prev,
        scheduledDates: [...prev.scheduledDates, date.toISOString()],
      }));
    }
  };

  const handleRemoveSchedule = (index: number) => {
    setNewCampaign(prev => ({
      ...prev,
      scheduledDates: prev.scheduledDates.filter((_, i) => i !== index),
    }));
  };

  const handleSelectLead = async (leads: Lead[]) => {
    console.log(`CampaignSection: ${leads.length} leads selecionados para a campanha`);
    const newRecipients = [...newCampaign.recipients];
    const updatedLeads = [];

    for (const lead of leads) {
      if (!newRecipients.includes(lead.email)) {
        newRecipients.push(lead.email);
        
        // Atualiza o estado do lead para "Em Campanha"
        try {
          // Cria uma nova interação para o lead
          const newInteraction = {
            id: `${lead.id}-${Date.now()}`,
            date: new Date().toISOString(),
            type: 'note' as const,
            direction: 'outgoing' as const,
            content: `Adicionado à campanha: ${newCampaign.name || 'Nova Campanha'}`
          };
          
          // Atualiza o lead com a nova interação
          const updatedLead = await marketingService.updateLead(lead.id, {
            ...lead,
            status: 'Em Campanha',
            interactions: [
              ...(lead.interactions || []),
              newInteraction
            ]
          });
          
          // Atualiza o lead no Redux
          dispatch(updateLead(updatedLead));
          
          updatedLeads.push(updatedLead);
          console.log(`Lead ${lead.name} atualizado com sucesso para a campanha ${newCampaign.name || 'Nova Campanha'}`);
        } catch (error) {
          console.error('Erro ao atualizar lead:', error);
        }
      }
    }

    setNewCampaign(prev => ({
      ...prev,
      recipients: newRecipients
    }));
    setLeadSelectionDialogOpen(false);
  };

  const handleRemoveRecipient = (email: string) => {
    setNewCampaign(prev => ({
      ...prev,
      recipients: prev.recipients.filter(recipient => recipient !== email),
    }));
  };

  const handleImproveTextWithAI = async () => {
    if (!newCampaign.content.trim()) return;
    setLoading(true);
    try {
      const { improvedText } = await marketingService.improveTextWithAI(newCampaign.content);
      setNewCampaign(prev => ({ ...prev, content: improvedText }));
    } catch (error) {
      console.error('Erro ao melhorar texto com IA:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCampaign = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      console.log('Enviando campanha com os seguintes dados:');
      console.log('Nome:', newCampaign.name);
      console.log('Tipo:', newCampaign.type);
      console.log('Assunto:', newCampaign.subject);
      console.log('Destinatários:', newCampaign.recipients);
      console.log('Enviar Agora:', newCampaign.sendNow);
      console.log('Datas agendadas:', newCampaign.scheduledDates);
      
      // Cria um FormData para enviar a campanha
      const formData = new FormData();
      formData.append('name', newCampaign.name);
      formData.append('type', newCampaign.type);
      formData.append('subject', newCampaign.subject);
      formData.append('content', newCampaign.content);
      formData.append('recipients', JSON.stringify(newCampaign.recipients));
      formData.append('sendNow', newCampaign.sendNow.toString());
      
      // Define o status com base na opção "Enviar Agora"
      let status = 'draft';
      if (newCampaign.sendNow) {
        status = 'sent';
      } else if (newCampaign.scheduledDates.length > 0) {
        status = 'scheduled';
      }
      formData.append('status', status);
      console.log('Status definido:', status);
      
      // Se não for enviar agora, adiciona as datas agendadas
      if (!newCampaign.sendNow) {
        formData.append('scheduledDates', JSON.stringify(newCampaign.scheduledDates));
      }
      
      // Adiciona os arquivos de mídia
      mediaFiles.forEach((file, index) => {
        formData.append(`media_${index}`, file.file);
      });
      
      // Envia a campanha
      const campaign = await marketingService.createCampaign(formData);
      console.log('Campanha criada com sucesso:', campaign);
      console.log('Status da campanha criada:', campaign.status);
      
      // Armazena a campanha criada para exibir no diálogo
      setCreatedCampaign(campaign);
      
      // Limpa o formulário
      setNewCampaign({
        name: '',
        type: 'email',
        subject: '',
        content: '',
        recipients: [],
        scheduledDates: [],
        sendNow: false,
      });
      setMediaFiles([]);
      
      // Abre o diálogo de sucesso
      setSuccessDialogOpen(true);
      
      // Cria notificação na página de notificações
      createNotification({
        ...NOTIFICATION_TYPES.CAMPAIGN.CREATED,
        message: `Campanha "${campaign.name}" criada com sucesso para ${campaign.recipients.length} destinatários`,
        link: '/marketing/historico',
        linkText: 'Ver campanhas'
      });
    } catch (error) {
      console.error('Erro ao enviar campanha:', error);
      alert('Erro ao criar campanha. Tente novamente.');
      
      // Cria notificação de erro na página de notificações
      createNotification({
        ...NOTIFICATION_TYPES.CAMPAIGN.ERROR,
        message: 'Ocorreu um erro ao criar a campanha. Tente novamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    setCreatedCampaign(null);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                <ScheduleIcon sx={{ mr: 1 }} />
                Nova Campanha
              </Typography>
            </Box>

            <form onSubmit={handleSubmitCampaign}>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Nome da Campanha"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  fullWidth
                  required
                />

                <FormControl fullWidth required>
                  <InputLabel>Tipo de Campanha</InputLabel>
                  <Select
                    value={newCampaign.type}
                    label="Tipo de Campanha"
                    onChange={(e) => setNewCampaign({ ...newCampaign, type: e.target.value as 'email' | 'whatsapp' })}
                  >
                    <MenuItem value="email">
                      <Box display="flex" alignItems="center">
                        <EmailIcon sx={{ mr: 1 }} />
                        E-mail
                      </Box>
                    </MenuItem>
                    <MenuItem value="whatsapp">
                      <Box display="flex" alignItems="center">
                        <WhatsAppIcon sx={{ mr: 1 }} />
                        WhatsApp
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {newCampaign.type === 'email' && (
                  <TextField
                    label="Assunto"
                    value={newCampaign.subject}
                    onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
                    fullWidth
                    required
                  />
                )}

                <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                  <Box sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {/* Barra de ferramentas aqui */}
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleImproveTextWithAI}
                      startIcon={<AutoFixHighIcon />}
                      disabled={!newCampaign.content.trim() || loading}
                    >
                      Melhorar com IA
                    </Button>
                  </Box>
                  <RichTextEditor
                    value={newCampaign.content}
                    onChange={(content) => setNewCampaign({ ...newCampaign, content })}
                    placeholder="Digite o conteúdo da sua campanha..."
                  />
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Produtos
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    Selecione um produto para incluir na campanha
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<ShoppingCartIcon />}
                    fullWidth
                    onClick={() => {
                      // Abrir diálogo de seleção de produtos
                      window.dispatchEvent(new CustomEvent('openProductDialog', {
                        detail: {
                          onSelect: (product: any) => {
                            // Adicionar informações do produto ao conteúdo
                            const productInfo = `
<h3>${product.name}</h3>
<p>Preço: R$ ${product.price.toFixed(2)}</p>
<p>${product.description || ''}</p>
`;
                            setNewCampaign(prev => ({
                              ...prev,
                              content: prev.content + productInfo
                            }));
                          }
                        }
                      }));
                    }}
                  >
                    Selecionar Produto
                  </Button>
                </Box>

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Mídia
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    Suporta até 10 imagens ou 1 vídeo por campanha
                  </Typography>
                  <input
                    accept="image/*,video/*"
                    style={{ display: 'none' }}
                    id="media-upload"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleMediaUpload}
                  />
                  <label htmlFor="media-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={mediaFiles[0]?.type === 'video' ? <VideocamIcon /> : <PhotoCameraIcon />}
                      fullWidth
                      disabled={mediaFiles.length >= 10 || mediaFiles.some(m => m.type === 'video')}
                    >
                      {mediaFiles.length > 0 
                        ? `${mediaFiles.length} arquivo${mediaFiles.length > 1 ? 's' : ''} selecionado${mediaFiles.length > 1 ? 's' : ''}`
                        : 'Carregar Mídia'}
                    </Button>
                  </label>
                  {mediaFiles.length > 0 && (
                    <Box mt={2} display="grid" gridTemplateColumns="repeat(auto-fill, minmax(150px, 1fr))" gap={2}>
                      {mediaFiles.map((media) => (
                        <Box key={media.id} position="relative">
                          {media.type === 'image' ? (
                            <Box
                              component="img"
                              src={media.preview}
                              alt="Mídia da campanha"
                              sx={{
                                width: '100%',
                                height: 150,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid #ddd'
                              }}
                            />
                          ) : (
                            <Box
                              component="video"
                              src={media.preview}
                              controls
                              sx={{
                                width: '100%',
                                height: 150,
                                borderRadius: 1,
                                border: '1px solid #ddd'
                              }}
                            />
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveMedia(media.id)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'background.paper'
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Destinatários
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<PeopleIcon />}
                    fullWidth
                    sx={{ mb: 2 }}
                    onClick={() => setLeadSelectionDialogOpen(true)}
                  >
                    Buscar Lead
                  </Button>
                  {newCampaign.recipients.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {newCampaign.recipients.map((recipient, index) => (
                        <Chip
                          key={index}
                          label={recipient}
                          onDelete={() => handleRemoveRecipient(recipient)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={newCampaign.sendNow}
                      onChange={(e) => setNewCampaign({ ...newCampaign, sendNow: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Enviar Agora"
                />

                {!newCampaign.sendNow && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Data e Hora de Agendamento
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      Mantenha a tecla Shift pressionada para selecionar um intervalo de datas
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                      <DateTimePicker
                        label="Adicionar Data e Hora"
                        onChange={handleAddSchedule}
                        sx={{ width: '100%', mb: 2 }}
                      />
                    </LocalizationProvider>
                    {newCampaign.scheduledDates.length > 0 && (
                      <Box display="flex" flexDirection="column" gap={1}>
                        {newCampaign.scheduledDates
                          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                          .map((date, index) => (
                            <Chip
                              key={index}
                              label={new Date(date).toLocaleString('pt-BR')}
                              onDelete={() => handleRemoveSchedule(index)}
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                      </Box>
                    )}
                  </Box>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || !newCampaign.content.trim() || !newCampaign.name.trim() || newCampaign.recipients.length === 0 || (!newCampaign.sendNow && newCampaign.scheduledDates.length === 0)}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : 'Criar Campanha'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Grid>

      <LeadSelectionDialog
        open={leadSelectionDialogOpen}
        onClose={() => setLeadSelectionDialogOpen(false)}
        onSelectLead={handleSelectLead}
      />

      {/* Diálogo de Sucesso */}
      <Dialog
        open={successDialogOpen}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" color="success.main">
            <CheckCircleIcon sx={{ mr: 1 }} />
            Campanha Criada com Sucesso!
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            A campanha "{createdCampaign?.name}" foi criada com sucesso e está pronta para ser enviada.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Você pode visualizar e gerenciar esta campanha no histórico de campanhas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccessDialog} variant="contained" color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default CampaignSection; 